/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useConfirmationModal } from "@imminently/react/context/modals";
import { useNotify } from "@imminently/react/store/notifications";
import { Text } from "@imminently/react/ui/core/text";
import { Box, Button, Grid, Stack, Tooltip } from "@mui/material";
import { capitalize } from "lodash";
import moment from "moment";
import { useNavigate } from "react-router";
import Select from "react-select";
import { useTranslation } from "react-i18next";

import { AnonymousUserIcon, ArchivefolderIcon, TimeIcon, CalendartimeIcon, TooltipIcon } from "@/assets/icons";
import { Icon } from "@/components/Icon";
import PatientATSScore from "@/components/Patient/PatientATSScore";
import PatientDetail from "@/components/Patient/PatientDetail";
import PatientNotifications from "@/components/Patient/PatientNotifications";
import { formatDuration, getEncounterStatusForDisplaying } from "@/global";
import { useArchivePatientModal } from "@/modals/archivePatient";
import { useDeanonymisePatientModal } from "@/modals/deanonymisePatient";
import { PatientEncounterChangeListener } from "@/pages/patient/patient";
import { FRAILTY_TYPES, PAIN_TYPES } from "@/resources/constants";
import { getLengthOfStay, getTimeSinceTriage, getTimeToTriage } from "@/resources/encounters";
import { Location, PatientEncounter } from "@/resources/types";
import { encounterService } from "@/services";
import { useSettingsContext } from "@/pages/settings/SettingsProvider";
import { css, Theme } from "@/theme";
import { Delegates } from "./PatientDetailBlock_Delegates";
import { Medicare } from "./PatientDetailBlock_Medicare";
import { useConfigContext } from "@imminently/react/context/config";
import { ReactNode } from "react";
import { useConciergeMode } from "@/contexts/ConciergeModeContext";
import { ExternalRegisterSecureDetailsModal } from "@/components/externalregister/ExternalRegisterSecureDetailsModal";
import { useExternalRegisterSecureDetailsModalState } from "@/components/externalregister/useExternalRegisterSecureDetailsModalState";
import {
  canOpenExternalRegisterDetails,
  canRenderExternalRegisterClinicalIndicator,
} from "@/util/externalRegister";

const styles = {
  container: {
    px: 4,
    py: 3,
    position: "relative",
    borderRadius: "0 0 1rem 1rem",
    background: "#fafbff",
  },
  infoBoxLeft: css`
    text-align: left;
    font-family: "Lato";
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
  `,
  infoBoxRight: css`
    text-align: right;
    line-height: 100%;
    margin-left: auto;
  `,
  losBox: css`
    background: #c3c8de;
    color: #161b32;
    padding: 1rem 1rem;
    margin-bottom: 1rem;
    border-radius: 0.5rem;
    max-height: 64px;
  `,
  alertBadge: (theme: Theme) => css`
    font-family: "Lato";
    font-style: normal;
    font-weight: 700;
    font-size: 12px;
    color: ${theme.palette.common.white};
    display: inline;
    padding: 0.25rem 0.5rem;
    margin-left: 0.5rem;
    border-radius: 0.5rem;
    background-color: #ff4b42;
  `,
  alertContainer: {
    py: 1,
    flexWrap: "wrap",
    borderRadius: 1,
    border: "1px solid #c3c8de",
    padding: 1,
    gap: 0.5,
    minHeight: 50,
    display: "flex",
    alignItems: "center",
  },
  borderContainer: css`
    > div {
      border-radius: 0;
      border: 1px solid #c3c8de;
      border-top: none;
      padding-left: 1rem;
      padding-right: 1rem;
    }

    > div {
      &:first-of-type {
        border-radius: 8px 8px 0 0;
        border-top: 1px solid #c3c8de;
      }

      &:last-child {
        border-radius: 0 0 8px 8px;
      }

      &:only-child {
        border-radius: 16px;
      }
    }
  `,
  archiveButton: css`
    background: #f0f3ff;
    color: #161b32;
    font-family: "Lato";
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    width: 100%;
    margin-bottom: 1rem;

    &:hover {
      background: #c3c8de;
    }
  `,
  deleteButton: css`
    font-family: "Lato";
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    width: 100%;
  `,
};

type PatientDetailBlockProps = {
  encounter: PatientEncounter;
  onEncounterChange: PatientEncounterChangeListener;
};

const mapLocation = (location: Location) => ({
  value: location.id, // Send only the ID
  label: location.name,
});

const useDeletePatient = (encounter: PatientEncounter) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { error } = useNotify();
  const { mutateAsync } = encounterService.useDelete();
  const confirmation = useConfirmationModal();

  const name = `${encounter.patient?.firstName} ${encounter.patient?.lastName}`;
  const onDelete = () => {
    confirmation({
      title: t("patient.detail.deleteConfirmation.title"),
      message: (
        <Text>
          {t("patient.detail.deleteConfirmation.message", { patientName: name })}
        </Text>
      ),
      confirmProps: {
        text: t("common.buttons.delete"),
        color: "error",
      },
      cancelProps: {
        text: t("common.buttons.cancel"),
        variant: "text",
      },
    })
      .then(async () => {
        try {
          // Delete encounter
          await mutateAsync(encounter.id);
          // Redirect to patient list
          navigate("/patients");
        } catch (e) {
          console.log(e);
          error(t("patient.detail.toast.deleteFailed"));
        }
      })
      .catch(() => {
        // do nothing
      });
  };

  return onDelete;
};

const ArrivedTime = ({ encounter }: { encounter: PatientEncounter }) => {
  const { t } = useTranslation();
  let label = t("patient.detail.arrival.arrived");
  let value: ReactNode = t("patient.detail.arrival.fallback");
  let caption: ReactNode = null;
  const toolTip = null;

  // Validate that arrived exists, is a number, and resolves to a valid date
  if (encounter.arrived) {
    try {
      const arrivedValue = Number(encounter.arrived);

      if (!isNaN(arrivedValue) && isFinite(arrivedValue)) {
        const arrivedTime = moment(arrivedValue);

        // Check if moment parsed it as a valid date
        if (arrivedTime.isValid()) {
          const date = arrivedTime.format("DD/MM/YYYY");
          const time = arrivedTime.format("h:mm a");

          // Check status to determine label
          if (encounter.status === "expected") {
            label = t("patient.detail.arrival.expected");
            // Only show "past due" for expected patients
            // Check if the expected time is at least 1 minute in the past
            const oneMinuteAgo = moment().subtract(1, "minute");
            if (arrivedTime.isBefore(oneMinuteAgo)) {
              caption = (
                <Text variant="caption" sx={{ color: "white", fontWeight: 700, fontStyle: "italic", marginLeft: 0.5 }}>
                  {t("patient.detail.arrival.pastDue")}
                </Text>
              );
            }
          } else if (encounter.status === "didnotpresent") {
            label = t("patient.detail.arrival.expected");
            caption = (
              <Text variant="caption" sx={{ color: "white", fontWeight: 700, fontStyle: "italic", marginLeft: 0.5 }}>
                {t("patient.detail.arrival.didNotAttend")}
              </Text>
            );
          }

          value = (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span>{time}</span>
              <span>{date}</span>
            </div>
          );
          // temp disable this as its not adding much value
          // toolTip = arrivedTime.format("DD/MM/YYYY [at] h:mm a");
        }
      }
    } catch (error) {
      // Silently handle any unexpected errors in date conversion
      // Fall back to default "-" display
      console.warn("Error processing arrival time:", error);
    }
  }

  return (
    <Stack css={styles.losBox} direction="row" alignItems="center" gap={1}>
      <CalendartimeIcon width={20} height={20} style={{ flexShrink: 0, verticalAlign: "middle" }} />
      <Stack direction="column" sx={{ flex: 1, alignItems: "flex-start", gap: 0 }}>
        <Text css={styles.infoBoxLeft}>{label}</Text>
        {caption}
      </Stack>
      <Tooltip title={toolTip} placement="top" arrow>
        <Text css={styles.infoBoxRight} variant="h6">
          {value}
        </Text>
      </Tooltip>
    </Stack>
  );
};

const LOS = ({ encounter }: { encounter: PatientEncounter }) => {
  const { t } = useTranslation();
  const label = t("patient.detail.los.label");
  const los = getLengthOfStay(encounter);
  const timeToTriage = getTimeToTriage(encounter);
  const timeSinceTriage = getTimeSinceTriage(encounter);

  const value = los ? formatDuration(moment.duration(los)) : t("patient.detail.los.fallback");

  const tooltip = timeToTriage || timeSinceTriage ? (
    <Stack>
      {timeToTriage ? <Text>{t("patient.detail.los.timeToTriage")}{formatDuration(moment.duration(timeToTriage))}</Text> : null}
      {timeSinceTriage ? <Text>{t("patient.detail.los.timeSinceTriage")}{formatDuration(moment.duration(timeSinceTriage))}</Text> : null}
    </Stack>
  ) : null;

  return (
    <Stack css={styles.losBox} direction="row" alignItems="center" gap={1}>
      <TimeIcon style={{ verticalAlign: "middle" }} />
      <Text css={styles.infoBoxLeft}>{label}</Text>
      <Tooltip arrow placement="right" title={tooltip}>
        <Text css={styles.infoBoxRight} variant="h3">
          {value}
        </Text>
      </Tooltip>
    </Stack>
  );
};

const Status = ({ encounter }: { encounter: PatientEncounter }) => {
  const { displayStatus, tooltip, statusList } = getEncounterStatusForDisplaying(encounter);
  const capitalizedDisplayStatus = capitalize(displayStatus);

  return (
    <Stack css={styles.losBox} direction="row" alignItems="center" gap={1}>
      <TooltipIcon width={20} height={20} style={{ flexShrink: 0, verticalAlign: "middle" }} />
      <Text css={styles.infoBoxLeft}>Status</Text>
      <Tooltip arrow placement="right" title={statusList.length > 1 ? tooltip : ""}>
        <Text css={styles.infoBoxRight} variant="h6">
          {capitalizedDisplayStatus}
        </Text>
      </Tooltip>
    </Stack>
  );
};

export const PatientDetailBlock = (props: PatientDetailBlockProps) => {
  const { t } = useTranslation();
  const { encounter, onEncounterChange } = props;
  const onDelete = useDeletePatient(encounter);
  const openArchivePatient = useArchivePatientModal();
  const openDeanonymisePatient = useDeanonymisePatientModal();
  const config = useConfigContext();
  const { settings } = useSettingsContext();
  const { isConciergeMode } = useConciergeMode();
  const {
    detailsModalState,
    openExternalRegisterDetails,
    closeExternalRegisterDetails,
  } = useExternalRegisterSecureDetailsModalState();
  const locations = ((settings as any)?.locations ?? []) as Location[];

  const onOpenModal = (callback: (props: any) => void) => () => {
    callback({ encounter });
  };

  const handleLocationChange = (locationId: string) => {
    // Send only the location ID to the backend
    onEncounterChange("location", locationId, "location.id");
  };

  const allergies = encounter.patient?.allergies;
  const encounterDischarged = encounter.status === "discharged";
  const encounterArchived = encounter.status === "disabled";
  // TODO: Check if user is part of care team
  const isPartOfCareTeam = true;
  // also detect anonymous patients by masked names since atm. the backend deletes the anonymous flag
  const isPatientAnonymous = (encounter.patient?.anonymous) || (encounter.patient?.firstName === "****" && encounter.patient?.lastName === "****");
  const canRenderExternalRegisterClinical = canRenderExternalRegisterClinicalIndicator({ isConciergeMode });
  const canOpenExternalRegisterSecureDetails = canOpenExternalRegisterDetails({ isConciergeMode });

  const fieldsDisabled =
    encounterDischarged || encounterArchived || !!encounter.triaged;


  return (
    <Box flex={1}>
      <PatientNotifications
        encounter={encounter}
        showExternalRegisterAlerts={canRenderExternalRegisterClinical}
        onOpenExternalRegisterDetails={canOpenExternalRegisterSecureDetails
          ? (alertId) => {
            const patientId = encounter.patient?.id;
            if (patientId) {
              openExternalRegisterDetails(patientId, alertId);
            }
          }
          : undefined}
      />

      <Grid container alignItems="center" sx={styles.container}>
        <Grid item xs={12}>
          <PatientATSScore encounter={encounter} />
          <ArrivedTime encounter={encounter} />
          <LOS encounter={encounter} />
          <Status encounter={encounter} />
        </Grid>

        {!!allergies?.length && (
          <Grid item xs={12} py={1}>
            <Text variant="h5">{t("patient.detail.sections.allergies")}</Text>
            <Grid item xs={12} sx={styles.alertContainer}>
              {allergies.map((allergy, index) => (
                <Box key={index} css={styles.alertBadge}>
                  {allergy.description}
                </Box>
              ))}
            </Grid>
          </Grid>
        )}

        <Grid item xs={12} py={1}>
          <Text variant="h5">{t("patient.detail.sections.contacts")}</Text>
        </Grid>
        <Grid item xs={12} css={styles.borderContainer}>
          <PatientDetail
            onEncounterChange={onEncounterChange}
            encounter={encounter}
            name={t("patient.detail.fields.email")}
            valuePath="patient.email"
            readOnly={true}
          />
          <PatientDetail
            onEncounterChange={onEncounterChange}
            encounter={encounter}
            name={t("patient.detail.fields.phoneNumber")}
            valuePath="patient.phone"
            readOnly={true}
          />

          <Delegates delegates={encounter.delegates || []} />

          <PatientDetail
            onEncounterChange={onEncounterChange}
            encounter={encounter}
            valuePath="patient.umrn"
            name={t("patient.detail.fields.umrn")}
            readOnly={true}
          />
        </Grid>

        <Grid item xs={12} py={1}>
          <Text variant="h5">{t("patient.detail.sections.others")}</Text>
        </Grid>
        <Grid item xs={12} css={styles.borderContainer}>
          <Medicare
            encounter={encounter}
            disabled={encounterDischarged || encounterArchived}
          />
          <Grid item xs={12} py={1}>
            <Box display="flex" flexDirection="column" gap={1}>
              <Text variant="h6">{t("patient.detail.location.label")}</Text>
              {!fieldsDisabled && (
                <Box sx={{ width: "100%" }}>
                  <Select
                    value={encounter.location ? { value: encounter.location.id, label: encounter.location.name } : null}
                    onChange={(option: any) => handleLocationChange(option?.value)}
                    options={locations
                      .filter(location => location.status === "active")
                      .map(location => ({ value: location.id, label: location.name }))}
                    placeholder={t("patient.detail.location.placeholder")}
                    isClearable
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderRadius: "0.5rem",
                        border: "1px solid #c3c8de",
                        backgroundColor: "transparent",
                        minHeight: "40px",
                        height: "auto",
                      }),
                      container: (provided) => ({
                        ...provided,
                        fontSize: "1rem",
                        fontWeight: 400,
                        lineHeight: "1.4375em",
                        fontFamily: "Lato,Nunito,Roboto,Arial,sans-serif",
                      }),
                    }}
                  />
                </Box>
              )}
              {fieldsDisabled && (
                <Text variant="h6" color="text.secondary">
                  {encounter.location?.name || t("patient.detail.location.unknown")}
                </Text>
              )}
            </Box>
          </Grid>
        </Grid>

        <Grid item xs={12} py={1}>
          <Text variant="h5">{t("patient.detail.sections.scores")}</Text>
        </Grid>
        <Grid item xs={12} css={styles.borderContainer}>
          <PatientDetail
            onEncounterChange={onEncounterChange}
            encounter={encounter}
            name={t("patient.detail.fields.riskScore")}
            valuePath="riskScore.score"
            readOnly={true}
          />
          <PatientDetail
            onEncounterChange={onEncounterChange}
            encounter={encounter}
            name={t("patient.detail.fields.initialPainScore")}
            valuePath="pain"
            selectProps={{ defaultOptions: PAIN_TYPES }}
            readOnly={fieldsDisabled}
          />
          <PatientDetail
            onEncounterChange={onEncounterChange}
            encounter={encounter}
            name={t("patient.detail.fields.initialFrailtyScale")}
            valuePath="frailty"
            selectProps={{ defaultOptions: FRAILTY_TYPES }}
            readOnly={fieldsDisabled}
          />
        </Grid>

        <Grid item xs={12} mt={4}>
          {(isPartOfCareTeam) && (
            <Button
              css={styles.archiveButton}
              variant="contained"
              color="primary"
              onClick={onOpenModal(openDeanonymisePatient)}
              startIcon={<AnonymousUserIcon />}
            >
              {isPatientAnonymous ? t("patient.detail.actions.deanonymise") : t("patient.detail.actions.anonymise")}
            </Button>
          )}
          {!encounterArchived && (
            <Button
              css={styles.archiveButton}
              variant="contained"
              color="primary"
              onClick={onOpenModal(openArchivePatient)}
              startIcon={<ArchivefolderIcon />}
            >
              {t("patient.detail.actions.archive")}
            </Button>
          )}
          {/* @ts-ignore - Due to issue with config.env type not being set correctly in sub package */}
          {config.env !== "prod" && (
            <Button
              css={styles.deleteButton}
              variant="contained"
              color="primary"
              onClick={onDelete}
              startIcon={<Icon icon="delete" />}
            >
              {t("patient.detail.actions.delete")}
            </Button>
          )}
        </Grid>
      </Grid>
      <ExternalRegisterSecureDetailsModal
        open={detailsModalState.open}
        patientId={detailsModalState.patientId}
        alertId={detailsModalState.alertId}
        isConciergeMode={isConciergeMode}
        onClose={closeExternalRegisterDetails}
      />
    </Box>
  );
};
